/**
 * Helpers to read the HTTP status of a failed API call and to decide how a screen reacts to it
 * (FEAT-047 TASK-022, design.md section 2).
 *
 * - 403 / 404 on the MAIN call of a page or tab -> access denied state (AccessDenied).
 * - 401 is the session-expiry flow of the API client, never an access denied.
 * - Queries must not retry on 401 / 403 / 404: the answer will not change and the screen
 *   would stay in its loading state meanwhile.
 */

import { parseQuotaExceededInfo } from './quotaExceededText';

/**
 * HTTP status carried by a failed API call (ApiClientError exposes `status`), or undefined for
 * network and non-API errors. Duck-typed on purpose: no import of the API client.
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const { status } = error as { status: unknown };
    if (typeof status === 'number') return status;
  }
  return undefined;
}

/** True when the API refused the call by role (403) or by scope (404: outside the caller's scope). */
export function isAccessDeniedError(error: unknown): boolean {
  const status = getApiErrorStatus(error);
  return status === 403 || status === 404;
}

/** True for a 404 on a scoped resource (e.g. team outside the manager's teams). */
export function isOutOfScopeError(error: unknown): boolean {
  return getApiErrorStatus(error) === 404;
}

const NO_RETRY_STATUSES: ReadonlySet<number> = new Set([401, 403, 404]);

/**
 * TanStack Query `retry` function: no retry on 401 / 403 / 404, otherwise up to `maxRetries`
 * retries (the QueryClient default is 1).
 */
export function retryUnlessDenied(maxRetries = 1) {
  return (failureCount: number, error: unknown): boolean => {
    const status = getApiErrorStatus(error);
    if (status !== undefined && NO_RETRY_STATUSES.has(status)) return false;
    return failureCount < maxRetries;
  };
}

/** error_code / code carried by a failed API call, or undefined. Duck-typed like getApiErrorStatus. */
export function getApiErrorCode(error: unknown): string | undefined {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const { code } = error as { code: unknown };
    if (typeof code === 'string') return code;
  }
  return undefined;
}

/**
 * True for the 409 the API answers when a team slug is already used in the organization
 * (FEAT-047 TASK-048, Q-06). The code is E_SLUG_TAKEN; a legacy E_CONFLICT counts only when it
 * points at the slug field (`details.slug`, a map field -> message), so other conflicts keep
 * their own handling. The server text is never shown: the screen uses its own i18n message.
 */
export function isSlugTakenError(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 409) return false;
  const code = getApiErrorCode(error);
  if (code === 'E_SLUG_TAKEN') return true;
  if (code !== 'E_CONFLICT') return false;
  if (typeof error === 'object' && error !== null && 'details' in error) {
    const { details } = error as { details: unknown };
    return typeof details === 'object' && details !== null && !Array.isArray(details) && 'slug' in details;
  }
  return false;
}

/**
 * True for the 422 E_VALIDATION the API answers when a team manager is not an administrator or
 * maintainer of the organization (FEAT-048 TASK-002): `details.manager_user_id` carries the
 * message. The server text is never shown: the screen uses its own i18n message.
 */
export function isManagerNotEligibleError(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 422 || getApiErrorCode(error) !== 'E_VALIDATION') return false;
  if (typeof error === 'object' && error !== null && 'details' in error) {
    const { details } = error as { details: unknown };
    return (
      typeof details === 'object' && details !== null && !Array.isArray(details) && 'manager_user_id' in details
    );
  }
  return false;
}

// --- FEAT-054 TASK-011 (cota) ---
const QUOTA_EXHAUSTED_CODES: ReadonlySet<string> = new Set(['E_QUOTA_EXCEEDED', 'E_GATEWAY_QUOTA_EXHAUSTED']);

/** True for a 429 whose code says the token quota is exhausted. */
export function isQuotaExhaustedError(status: number | undefined, code: string | undefined): boolean {
  return status === 429 && code !== undefined && QUOTA_EXHAUSTED_CODES.has(code);
}

/**
 * Grace data of a 429 quota error (`details.grace_mode`, `grace_pct` string, `reset_at`), or null
 * for other errors and for servers before FEAT-054 (the server message is kept then).
 */
export function getQuotaExceededInfo(status: number | undefined, code: string | undefined, details: unknown) {
  if (status !== 429 || code === undefined || !QUOTA_EXHAUSTED_CODES.has(code)) return null;
  if (typeof details !== 'object' || details === null || Array.isArray(details)) return null;
  const d = details as Record<string, unknown>;
  return parseQuotaExceededInfo({ graceMode: d['grace_mode'], gracePct: d['grace_pct'], resetAt: d['reset_at'] });
}
// --- fim FEAT-054 TASK-011 (cota) ---

/**
 * True for a 429 from the rate limiter (the screen may count down to the next try).
 * A 429 E_QUOTA_EXCEEDED is a quota, not a limiter: it renews in days and has no countdown.
 */
export function isRateLimitedError(error: unknown): boolean {
  return getApiErrorStatus(error) === 429 && getApiErrorCode(error) !== 'E_QUOTA_EXCEEDED';
}

/** Retry-After seconds of a rate-limited call (null when the header was absent or unreadable). */
export function getRetryAfterSeconds(error: unknown): number | null {
  if (typeof error === 'object' && error !== null && 'retryAfterSeconds' in error) {
    const { retryAfterSeconds } = error as { retryAfterSeconds: unknown };
    if (typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0) return retryAfterSeconds;
  }
  return null;
}

/**
 * Coarse class of a failed load, for screens that must say something readable without showing
 * the server text or a code (FEAT-047 TASK-078). Each screen maps the class to its own i18n id.
 */
export type ReadableErrorKind = 'network' | 'server' | 'unavailable' | 'unknown';

const UNAVAILABLE_CODES: ReadonlySet<string> = new Set([
  'E_SERVICE_UNAVAILABLE',
  'E_PROVIDER_UNAVAILABLE',
  'E_UPSTREAM_UNAVAILABLE',
]);
const NETWORK_CODES: ReadonlySet<string> = new Set([
  'E_NETWORK',
  'E_TIMEOUT',
  'E_STREAM_INTERRUPTED',
  'E_STREAM_DISCONNECTED',
]);
const SERVER_CODES: ReadonlySet<string> = new Set(['E_INTERNAL', 'E_INTERNAL_ERROR']);

/** Class from an API/stream error_code alone (stream errors carry no HTTP status). */
export function classifyErrorCode(code: string | undefined): ReadableErrorKind {
  if (!code) return 'unknown';
  if (UNAVAILABLE_CODES.has(code)) return 'unavailable';
  if (NETWORK_CODES.has(code)) return 'network';
  if (SERVER_CODES.has(code)) return 'server';
  return 'unknown';
}

/**
 * Class of a thrown error: an API error by status and code (503 / E_SERVICE_UNAVAILABLE ->
 * unavailable, other 5xx -> server), anything without a status (fetch TypeError, abort) -> network.
 */
export function classifyReadableError(error: unknown): ReadableErrorKind {
  const status = getApiErrorStatus(error);
  const byCode = classifyErrorCode(getApiErrorCode(error));
  if (status === undefined) return error instanceof Error ? 'network' : 'unknown';
  if (status === 503 || byCode === 'unavailable') return 'unavailable';
  if (status >= 500) return 'server';
  return byCode;
}

// --- FEAT-055 TASK-012 (not_listed) ---
/** Data of a 403 E_MODEL_NO_ACCESS whose model is no longer listed by the connected key. */
export interface NotListedInfo {
  /** Substitute model (ai_models.id) the server suggests, or null when there is none. */
  suggestedModelId: string | null;
}

/**
 * Reads the 403 E_MODEL_NO_ACCESS with `details.reason === 'not_listed'` (FEAT-055). Other 403s,
 * and servers before FEAT-055, give null. The screen shows the server message (already readable)
 * and opens the model picker; it never switches the model by itself.
 */
export function getNotListedInfo(
  status: number | undefined,
  code: string | undefined,
  details: unknown,
): NotListedInfo | null {
  if (status !== 403 || code !== 'E_MODEL_NO_ACCESS') return null;
  if (typeof details !== 'object' || details === null || Array.isArray(details)) return null;
  const d = details as Record<string, unknown>;
  if (d['reason'] !== 'not_listed') return null;
  const suggested = d['suggested_model_id'];
  return { suggestedModelId: typeof suggested === 'string' && suggested ? suggested : null };
}
// --- fim FEAT-055 TASK-012 (not_listed) ---

// --- FEAT-053 TASK-043 (so listagem) ---
/**
 * True for the 403 E_MODEL_NO_ACCESS with `details.reason === 'provider_listing_only'` (the model belongs to a
 * provider that only lists models, FEAT-053 TASK-041). Any other shape (no details, details not an object,
 * another reason) is false. The screen shows its own i18n text, never the server message.
 */
export function isProviderListingOnlyError(
  status: number | undefined,
  code: string | undefined,
  details: unknown,
): boolean {
  if (status !== 403 || code !== 'E_MODEL_NO_ACCESS') return false;
  if (typeof details !== 'object' || details === null || Array.isArray(details)) return false;
  return (details as Record<string, unknown>)['reason'] === 'provider_listing_only';
}
// --- fim FEAT-053 TASK-043 (so listagem) ---
